import type { TOC } from '@ember/component/template-only';
import Component from '@glimmer/component';
import type { ComponentLike } from '@glint/template';

export class TimelineItem {
  constructor(
    public Component: ComponentLike,
    public options?: { dotStyles?: string }
  ) {}
}

/** Renders the vertical line */
const Line = <template>
  <div class='bg-grey m-auto h-full w-[1px]'></div>
</template>;

interface DotSig {
  Args: {
    /** Renders a larger dot */
    large?: boolean;

    /**
     * Color of the dot
     * @default "grey"
     * */
    color?: 'purple-medium';
  };
}

/** Renders the dot with the character "●" */
class Dot extends Component<DotSig> {
  get color() {
    return this.args.color == 'purple-medium'
      ? 'text-purple-medium'
      : 'text-grey';
  }

  get styles() {
    const styles = [this.color];

    if (this.args.large) styles.push('text-xl');

    return styles.join(' ');
  }

  <template>
    <div class='text-grey relative top-[-100%] {{this.styles}}'>●</div>
  </template>
}

interface ThreadSig {
  Args: {
    /** @see DotSig['Args']['large'] */
    largeDot?: DotSig['Args']['large'];
    /** @see DotSig['Args']['color'] */
    dotColor: DotSig['Args']['color'];
  };
}

/** Renders the dot and the vertical line */
const Thread = <template>
  <div class='min-w-4 text-center'>
    <Line />
    <Dot @large={{@largeDot}} @color={{@dotColor}} />
  </div>
</template> satisfies TOC<ThreadSig>;

/** Yields its content at the right of the Thread */
const Content = <template>
  <div class='flex-grow' ...attributes>
    {{yield}}
  </div>
</template> satisfies TOC<{ Element: HTMLDivElement; Blocks: { default: [] } }>;

interface EntrySig {
  Args: {
    /** Use this if you want a custom style for the <div> wrapping your content
     * @default "mb-4"
     */
    contentStyles?: string;

    /** @see ThreadSig['Args']['large'] */
    largeDot?: ThreadSig['Args']['largeDot'];
    /** @see ThreadSig['Args']['color'] */
    dotColor?: ThreadSig['Args']['dotColor'];
  };
  Blocks: { default: [] };
}

/**
 * The "Entry" component is yieled by the main "Timeline" component
 *
 * You will use it to render custom content in the right side of the Timeline
 * */
class Entry extends Component<EntrySig> {
  get contentStyles() {
    return this.args.contentStyles ?? 'mb-4';
  }

  <template>
    <div class='flex gap-3' data-test-timeline-entry>
      <Thread @largeDot={{@largeDot}} @dotColor={{@dotColor}} />
      <Content class={{this.contentStyles}}>{{yield}}</Content>
    </div>
  </template>
}

/**
 * This component renders a thread (a vertical line and a dot) on the left
 * and a custom content on the right
 *
 * <Timeline as |Entry|>
 *   <Entry>
 *     The content of my Entry goes here…
 *   </Entry>
 *
 *   <Entry @contentStyles="mb-2">
 *     Another content with less bottom margin
 *   </Entry>

*   <Entry @largeDot={{true}} @dotColor="purple">
 *     The dot "●" in the thread will be larger and purple
 *   </Entry>
 * </Timeline>
 */
const Timeline = <template>
  <div class='flex flex-col'>
    {{yield Entry}}
  </div>
</template> satisfies TOC<{ Blocks: { default: [typeof Entry] } }>;

export default Timeline;
